const axios = require('axios');
const qs = require('qs');
import {hungarianRoundNumbers} from "../helpers/hungarianRoundNumbers";

const extractServiceFees = (obj) => {
    const result = {};

    for (const key in obj) {
        if (key.includes('Sz')) {
            const newKey = key.replace('Sz', '');
            result[newKey] = obj[key];
        }
    }

    return result;
}
/**
 * Get the VAT code based on the VAT percentage.
 * @param {number} vatPercentage - The VAT percentage.
 * @returns {string} The corresponding VAT code.
 */
const getVATCode = (vatPercentage) => {
    if (vatPercentage === 5) return "A";
    if (vatPercentage === 18) return "B";
    if (vatPercentage === 27) return "C";
    if (vatPercentage === 0) return "E";
};

/**
 * Format text with specified formats.
 * @param {string} text - The text to format.
 * @param {string[]} formats - The formats to apply.
 * @returns {string} The formatted text.
 */
const formatText = (text, formats) => {
    // Define the available formatting options
    const formatCommands = {
        left: '[0x81:Left][CR]',
        center: '[0x82:Center][CR]',
        right: '[0x84:Right][CR]',
        bold: '[0x88:Bold][CR]',
        doubleHeight: '[0x90:Double-Height][CR]',
        underline: '[0xA0:Under-Line][CR]',
        centerBold: '[0x8A:Center,Bold][CR]',
        important: '[0x8A:Center,Bold,Under-Line,Double-Height][CR]',
    };

    // Apply all the format commands to the text
    const appliedFormats = formats.map(format => formatCommands[format]).join('');
    return `${appliedFormats}${text}[LF]`;
};

/**
 * Get the payment type description.
 * @param {string} paymentType - The payment type.
 * @returns {string} The payment type description.
 */
const getPaymentType = (paymentType) => {
    switch (paymentType) {
        case 'cash':
            return 'Készpénz!';
        case 'creditCard':
            return 'Bankkártya!';
        case 'szepCreditCard':
            return 'Bankkártya futárnál!';
    }
}

export const FiscatPrinter = {
    /**
     * Test the connection to the Fiscat printer.
     * @returns {Promise<AxiosResponse>} The response from the printer.
     */
    async testConnection() {
        const data = qs.stringify({command: 'rs'});
        return await axios.post('http://127.0.0.1:15000/Request', data, {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            },
            timeout: 10000 // 5 seconds timeout
        });
    },

    /**
     * Open the day on the Fiscat printer.
     */
    async openDay() {
        try {
            // Import the background task trigger function
            const { triggerFiscalPrinter } = require('../helpers/BackgroundTasks');

            // Use the background task to open the day
            return triggerFiscalPrinter(null, 'openDay');
        } catch (error) {
            console.error('Error triggering fiscal printer open day:', error);

            // Fall back to direct implementation
            if ((await FiscatPrinter.testConnection()).status !== 200) {
                alert("A Fiscat nyomtató nincs csatlakoztatva");
                return console.error("Fiscat printer is not connected");
            }
            const data = qs.stringify({command: 'fod'});

            return axios.post('http://127.0.0.1:15000/Request', data, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            });
        }
    },

    /**
     * Close the day on the Fiscat printer.
     */
    async closeDay() {
        try {
            // Import the background task trigger function
            const { triggerFiscalPrinter } = require('../helpers/BackgroundTasks');

            // Use the background task to close the day
            return triggerFiscalPrinter(null, 'closeDay');
        } catch (error) {
            console.error('Error triggering fiscal printer close day:', error);

            // Fall back to direct implementation
            if ((await FiscatPrinter.testConnection()).status !== 200) {
                alert("A Fiscat nyomtató nincs csatlakoztatva");
                return console.error("Fiscat printer is not connected");
            }
            const data = qs.stringify({command: 'pgr\t1\t19'});
            const data2 = qs.stringify({command: 'fcd'});
            const data3 = qs.stringify({command: 'Z#CLOSE'});
            await axios.post('http://127.0.0.1:15000/Request', data2, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            })
            await axios.post('http://127.0.0.1:15000/Request', data3, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            })
            return new Promise((resolve) => {
                axios.post('http://127.0.0.1:15000/Request', data, {
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded'
                    }
                }).then(resolve);
            });
        }
    },

    /**
     * Print delivery information on the Fiscat printer.
     * @param {Object} orderData - The order data.
     */
    async printDeliveryInfo(orderData) {
        try {
            // Import the background task trigger function
            const { triggerFiscalPrinter } = require('../helpers/BackgroundTasks');

            // Use the background task to print delivery info
            return triggerFiscalPrinter(orderData, 'printDeliveryInfo');
        } catch (error) {
            console.error('Error triggering fiscal printer delivery info:', error);

            // Fall back to direct implementation
            if ((await FiscatPrinter.testConnection()).status !== 200) {
                alert("A Fiscat nyomtató nincs csatlakoztatva");
                return console.error("Fiscat printer is not connected");
            }

            // Construct the order comment
            let orderComment = "";
            if (orderData.addressInfo.deliveryInstructions) {
                orderComment = orderComment + ", " + orderData.addressInfo.deliveryInstructions;
            }
            if (orderData.addressInfo.other && orderData.addressInfo.other.info) {
                orderComment = orderComment + ", " + orderData.addressInfo.other.info;
            }
            if (orderData.addressInfo.other && orderData.addressInfo.other.doorBell) {
                orderComment = orderComment + ", " + orderData.addressInfo.other.doorBell;
            }
            if (orderData.comment) {
                orderComment = orderComment + ", " + orderData.comment;
            }
            if (orderData.comments.length > 0) {
                orderComment = orderComment + ", " + orderData.comments.join(", ");
            }

            // Construct the commands array
            const commands = [
                'onfr',
                'pnft' +
                formatText('#' + orderData.orderCode + "\n", ['center']) +
                formatText(orderData.userInfo.name.first + " " + orderData.userInfo.name.last + "\n", ['centerBold']) +
                formatText(orderData.userInfo.mobilePhone || orderData.userInfo.phone + "\n", ['center']) +
                formatText(orderData.addressInfo.address.zip ? orderData.addressInfo.address.zip : orderData.addressInfo.address + '\n\n', ['center']) +
                (orderComment.length > 0 ? formatText(orderComment + '\n\n\n', ['center']) : '\n\n\n') +
                formatText(getPaymentType(orderComment.paymentType), ['important'])
            ];

            // Send each command to the printer with a delay
            for (let i = 0; i < commands.length; i++) {
                const command = commands[i];
                const data = qs.stringify({command: command});
                await new Promise(resolve => {
                    setTimeout(() => {
                        axios.post('http://127.0.0.1:15000/Request', data, {
                            headers: {
                                'Content-Type': 'application/x-www-form-urlencoded'
                            }
                        }).then(resolve);
                    }, 300);
                });
            }
            return true;
        }
    },

    /**
     * Print the receipt on the Fiscat printer.
     * @param {Object} orderData - The order data.
     */
    async printReceipt(orderData) {
        if ((await FiscatPrinter.testConnection()).status !== 200) {
            alert("A Fiscat nyomtató nincs csatlakoztatva");
            return console.error("Fiscat printer is not connected");
        }

        // Validate that the order has items and a non-zero price
        if (!orderData.items || orderData.items.length === 0) {
            console.error("Empty order detected, skipping receipt printing");
            return false;
        }

        // Check if all items have zero price
        const hasNonZeroPrice = orderData.items.some(item => (item.remotePrice || item.price || 0) > 0);
        if (!hasNonZeroPrice) {
            console.error("Order with zero price detected, skipping receipt printing");
            return false;
        }

        let serviceFees = {};
        let itemsAndExtrasPerLine = [];
        let rowsCount = 0;

        try {
            // Construct the items and extras per line
            orderData.items.forEach(item => {
                rowsCount++;

                const vatPercentage = item?.item?.vat[orderData.orderType] || 27;
                const vatCode = getVATCode(vatPercentage); // Get VAT code based on percentage

                itemsAndExtrasPerLine.push(item);
                item.extras.forEach(extra => {
                    // Only add extras with a price > 0 to the fiscal receipt
                    if ((extra.remotePrice || extra.price) > 0) {
                        itemsAndExtrasPerLine.push({
                            ...extra,
                            remoteName: " +" + extra.remoteName || (extra.item.name.hu || extra.item.name.en),
                            name: " +" + extra.remoteName || (extra.item.name.hu || extra.item.name.en),
                            amount: (item.amount || item.quantity) || 1
                        });
                    }
                })
            });

            if(orderData.orderType === "dineIn" && orderData.grossSum) {
                // ServiceFee fix for dineIn orders where item.item.vat is not available
                // TODO: Alternate we will add vat directly to the items array
                serviceFees = extractServiceFees(orderData.grossSum);
            }

            // Construct the commands array
            let commands = [
                'ofr\t0\t' + orderData.orderCode.replace('-', '') + '\t0\t',
                'pcd\tT0\t\nRendelés száma:',
                'pcd\tT1\t\n#' + orderData.orderCode,
                'pcd\tT2\t\n---------------------',
                'pcd\tT3\t\nwww.happygastro.hu',
                'pcd\tT4\t\nÜzemelteti a Happy Gastro',
                itemsAndExtrasPerLine.map(item => 'sbt\t\t' + (item.remoteName || item.name || item.item.name?.hu || "GYŰJTŐ") + '\t\t\t\t\t' + (getVATCode(item.item.vat[orderData.orderType || "takeAway"] || 27) || ('C')) + '\t00\t' + (item.remotePrice || item.price || 0) + '\t' + (item.amount || item.quantity || 1) + '\t0')
            ];

            // Add the sub-total amount
            //commands = [...commands, 'st'];

            // Add service fee if applicable
            if (orderData.serviceFee > 0) {
                Object.keys(serviceFees).forEach(vat => {
                    commands.push('sbt\t\tSzervízdíj (' + vat + '%)\t\t\t\t\t' + getVATCode(Number(vat)) + '\t00\t' + parseFloat(serviceFees[vat]) + '\t1\t0');
                })
            }

            // Add delivery fee if applicable
            if (orderData.deliveryFee > 0) commands = [...commands, 'sbt\t\tSzállítási díj\t\t\t\t\tC\t00\t' + orderData.deliveryFee + '\t1\t0'];

            // Add discounts if applicable
            if (orderData.discounts && orderData.discounts.amount > 0) commands = [...commands, 'd\tD,' + (parseFloat((orderData.discounts.amount || 0).toString().replace(",", ".")))];
            if (orderData.discounts && orderData.discounts.percentage > 0) commands = [...commands, 'd\tM,' + orderData.discounts.percentage];

            // Add payment types
            commands = [
                ...commands,
                orderData.paymentTypes.map(paymentType => 'pmt\t\t\t' + (paymentType.paymentType === "cash" ? "P" : "N") + '\t' + (paymentType.amount > orderData(paymentType.paymentType === "cash" ? hungarianRoundNumbers(paymentType.amount) : paymentType.amount)))
            ]

            console.log(commands)

            // Send each command to the printer with a small delay
            return commands.forEach((command, index) => {
                const data = qs.stringify({command: command});
                setTimeout(() => axios.post('http://127.0.0.1:15000/Request', data, {
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded'
                    }
                }), (index * 250))
            });
        } catch (fiscatReceiptError) {
            alert("Fiscat error!")
            console.error("Fiscat Receipt Error: ", fiscatReceiptError);
            throw new Error(fiscatReceiptError);
        }
    }
}
